import { useState } from 'react';
import { Search, Edit, Trash2, Plus, MinusCircle } from 'lucide-react';
import { User } from '../../types';

// Mock user data
const mockUsers: User[] = [
  {
    id: '1',
    username: 'john_doe',
    email: 'john@example.com',
    credits: 150,
    createdAt: '2024-01-15T10:00:00Z',
    status: 'active',
  },
  {
    id: '2',
    username: 'jane_smith',
    email: 'jane@example.com',
    credits: 75,
    createdAt: '2024-02-01T15:30:00Z',
    status: 'active',
  },
  {
    id: '3',
    username: 'bob_wilson',
    email: 'bob@example.com',
    credits: 0,
    createdAt: '2024-03-10T08:45:00Z',
    status: 'disabled',
  },
];

export function UserManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState(0);

  const filteredUsers = mockUsers.filter(
    (user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreditAdjustment = (user: User) => {
    setSelectedUser(user);
    setShowCreditModal(true);
  };

  const handleStatusToggle = (userId: string) => {
    // Mock status toggle
    console.log('Toggle status for user:', userId);
  };

  const handlePasswordReset = (userId: string) => {
    // Mock password reset
    console.log('Reset password for user:', userId);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[--text-primary]">User Management</h1>
        <p className="mt-2 text-sm text-[--text-secondary]">
          Manage user accounts and permissions
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[--text-disabled]" />
          <input
            type="text"
            placeholder="Search users..."
            className="pl-10 pr-4 py-2 w-full border border-[--border-secondary] rounded-lg focus:ring-primary-500 focus:border-primary-500 bg-[--bg-primary] text-[--text-primary]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* User Table */}
      <div className="bg-[--bg-primary] shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-[--border-primary]">
          <thead className="bg-[--bg-secondary]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Credits
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Registered
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[--border-primary]">
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-[--text-primary]">
                        {user.username}
                      </div>
                      <div className="text-sm text-[--text-secondary]">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.status === 'active'
                        ? 'bg-success-100 text-success-800 dark:bg-[rgb(var(--success-900)/0.2)] dark:text-success-200'
                        : 'bg-error-100 text-error-800 dark:bg-[rgb(var(--error-900)/0.2)] dark:text-error-200'
                    }`}
                  >
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[--text-secondary]">
                  {user.credits}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-[--text-secondary]">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleCreditAdjustment(user)}
                      className="text-[--link-default] hover:text-[--primary-900] dark:text-[--primary-400] dark:hover:text-[--primary-300]"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleStatusToggle(user.id)}
                      className="text-warning-600 hover:text-warning-900 dark:text-warning-400 dark:hover:text-warning-300"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handlePasswordReset(user.id)}
                      className="text-error-600 hover:text-error-900 dark:text-error-400 dark:hover:text-error-300"
                    >
                      <MinusCircle className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Credit Adjustment Modal */}
      {showCreditModal && selectedUser && (
        <div className="fixed inset-0 bg-[rgb(var(--color-black-rgb)/0.5)] flex items-center justify-center">
          <div className="bg-[--bg-primary] rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-[--text-primary] mb-4">
              Adjust Credits for {selectedUser.username}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[--text-primary]">
                  Amount
                </label>
                <input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(parseInt(e.target.value))}
                  className="mt-1 block w-full border border-[--border-secondary] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-[--bg-primary]"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-[--text-primary] hover:bg-[--state-hover] rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Handle credit adjustment
                    setShowCreditModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}